import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faFileExport,
  faFilter,
  faSearch,
  faSortAlphaDown,
  faSortAlphaUp
} from "@fortawesome/free-solid-svg-icons";
import {Link} from "react-router-dom";

// Contexts
import {AuthContext} from "../../contexts/auth.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";
import usePrevious from "../../hooks/usePrevious.js";

// Utils
import {prettyDateInUserTimezone} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import BaseTable from "../../components/BaseTable.js";
import ModalExport from "../../components/ModalExport.js";
import ModalFacilityFilter from "../facilities/ModalFacilityFilters.js";
import Badge from "../../components/Badge.js";

// Style
import {voice} from "../../style/components/typography.js";
import {colors, pad} from "../../style/components/variables.js";
import {bp} from "../../style/components/breakpoints.js";
import {
  Inline,
  Text,
  TableFooter,
  TableWrapper,
  Small,
  SearchWrapper,
  Search,
  Button,
  Heading,
  TableMenu,
  SearchIcon,
  List,
  ListItem,
  Error,
  Warning,
  Abbr
} from "../../style/components/general.js";

const ModalViewAllFacilities = ({visible, setVisible}) => {
  const isMounted = useMountedState();

  const {atLeast, roleCanAccessResource} = useContext(AuthContext);

  const [tableData, setTableData] = useState();
  const [query, setQuery] = useState();
  const [orderBy, setOrderBy] = useState("asc");
  const [groupBy, setGroupBy] = useState("firstName");
  // Filters
  const [activeFilters, setActiveFilters] = useState();
  const [showDeleted, setShowDeleted] = useState(false);
  // Pagination
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTotal, setPageTotal] = useState(1);
  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showModalFilters, setShowModalFilters] = useState(false);
  const [showModalExport, setShowModalExport] = useState(false);

  const filterCount = useMemo(() => {
    let count = 0;
    if (activeFilters)
      Object.keys(activeFilters).map(filter => {
        count += activeFilters[filter]?.length || 0;
      });
    return count + showDeleted ? 1 : 0;
  }, [showDeleted, activeFilters]);

  const scrollPos = useRef();
  const scrollContainer = useRef();
  const tableContent = useRef();

  const {api, loading} = useApi("facilities");

  const limit = useRef(5);

  const prevOrderBy = usePrevious(orderBy);
  const prevGroupBy = usePrevious(groupBy);

  const formatTableData = useCallback(
    facilities =>
      facilities.map(({isDeleted, id, slug, name, type, details, checksheets, events}) => ({
        isDeleted,
        id: id,
        name: (
          <TableLink to={`/facilities/${slug}`} title={name}>
            {name}
          </TableLink>
        ),
        type: <List column>{type}</List>,
        jobs: details?.jobs?.length > 0 && (
          <List column>
            {details.jobs.map(job => (
              <ListItem key={job}>{job}</ListItem>
            ))}
          </List>
        ),
        checksheets: checksheets?.map(
          ({id: cId, name: cName, frequency, dateDue, draftCount, overdueCount}) => (
            <NotifyList key={cId} column>
              <TableLink to={`/facilities/${slug}/tasks`} title={cName}>
                {cName}
                {frequency?.name && <Frequency>({frequency.name})</Frequency>}
              </TableLink>
              <NotifyListItem>
                <Small>Due by {prettyDateInUserTimezone(dateDue)}</Small>
              </NotifyListItem>
              {draftCount > 0 && (
                <NotifyListItem>
                  <Warning>{draftCount} incomplete record(s)</Warning>
                </NotifyListItem>
              )}
              {overdueCount > 0 && (
                <NotifyListItem>
                  <Error>{overdueCount} overdue record(s)</Error>
                </NotifyListItem>
              )}
            </NotifyList>
          )
        ),
        events: events?.map(
          ({id: eId, name: eName, available, dateDue, draftCount, overdueCount}) => (
            <NotifyList key={eId} column>
              <TableLink to={`/facilities/${slug}/schedule`} title={eName}>
                {eName}
              </TableLink>
              {available && (
                <NotifyListItem>
                  <Small>Due by {prettyDateInUserTimezone(dateDue)}</Small>
                </NotifyListItem>
              )}
              {draftCount > 0 && (
                <NotifyListItem>
                  <Warning>{draftCount} incomplete record(s)</Warning>
                </NotifyListItem>
              )}
              {overdueCount > 0 && (
                <NotifyListItem>
                  <Error>{overdueCount} overdue record(s)</Error>
                </NotifyListItem>
              )}
            </NotifyList>
          )
        )
      })),
    []
  );

  const getTableData = useCallback(
    (p, l) => {
      const filter = {};

      const payload = {
        page: p || 1,
        limit: l ?? limit.current,
        orderBy: orderBy,
        groupBy: "name",
        withChecksheets: true,
        withEvents: true,
        withDeleted: showDeleted
      };

      if (query) filter.Search = query;
      if (activeFilters?.types) filter.Type = activeFilters.types;
      if (activeFilters?.checksheets) filter.Checksheet = activeFilters.checksheets;

      if (filter && Object.keys(filter).length > 0) payload.filter = JSON.stringify(filter);

      api.callGet(null, payload).then(({status, data}) => {
        if (status === 200) {
          const {facilities: facilityData, page, pages, total: cTotal} = data;

          const formatted = formatTableData(facilityData);

          setCurrentPage(page);
          setPageTotal(pages);
          setTotal(cTotal);
          setTableData(prev => (p === 1 ? formatted : [...prev, ...formatted]));
        }
      });
    },
    [api, activeFilters, formatTableData, limit, orderBy, query, showDeleted]
  );

  useEffect(() => {
    if (
      isMounted() &&
      prevOrderBy &&
      prevGroupBy &&
      (prevOrderBy !== orderBy || prevGroupBy !== groupBy)
    )
      getTableData(1);
  }, [isMounted, getTableData, prevOrderBy, prevGroupBy, orderBy, groupBy]);

  useEffect(() => {
    if (query || query === "") getTableData(1);
  }, [getTableData, query]);

  // Initial Load
  useEffect(() => {
    if (isMounted() && !tableData) {
      // modal padding and table menu
      const buffer = 120;
      const approxCellHeight = 125;
      // estimation for number of cards to overflow
      const approxRows = Math.ceil(
        (scrollContainer.current.clientHeight - buffer) / approxCellHeight
      );
      limit.current = approxRows;
      getTableData(1, approxRows);
    }
  }, [isMounted, tableData, getTableData]);

  // Load more on Scroll
  const handleScroll = useCallback(() => {
    const {scrollHeight, clientHeight} = scrollContainer.current;

    const maxScrollTop = scrollHeight - clientHeight;
    if (
      scrollContainer.current.scrollTop + 1 >= maxScrollTop &&
      currentPage < pageTotal &&
      !loading
    )
      getTableData(currentPage + 1);
  }, [currentPage, getTableData, loading, pageTotal]);

  useEffect(() => {
    const modal = scrollContainer.current;
    modal.addEventListener("scroll", handleScroll);
    return () => modal.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useLayoutEffect(() => {
    if (showModalFilters || showModalExport) {
      scrollPos.current = document.getElementById("modal").scrollTop;
      setShowModal(true);
    } else {
      scrollContainer.current.scrollTop = scrollPos.current;
      setShowModal(false);
    }
  }, [showModalFilters, showModalExport]);

  const handleSearch = e => {
    setCurrentPage(1);
    setPageTotal(1);
    setQuery(e.target.value);
  };

  const handleReload = () => {
    setCurrentPage(1);
    setPageTotal(1);
    getTableData(1);
  };

  if (showModal && showModalFilters) {
    return (
      <ModalFacilityFilter
        visible={showModalFilters}
        setVisible={setShowModalFilters}
        filters={activeFilters}
        setFilters={f => {
          setActiveFilters(f);
          setShowModalFilters(false);
          handleReload();
        }}
        showArchived={showDeleted}
        setShowArchived={setShowDeleted}
        hasBackButton
        goBack={() => setShowModalFilters(false)}
      />
    );
  }

  if (showModal && showModalExport)
    return (
      <ModalExport
        visible={showModalExport}
        setVisible={setShowModalExport}
        getExport={async exportParams => api.callGet(null, {...exportParams})}
        hasBackButton
        goBack={() => setShowModalExport(false)}
      />
    );

  return (
    <Modal ref={scrollContainer} visible={visible} setVisible={setVisible} fixedHeight>
      <TableWrapper>
        <TableMenuRow>
          <StyledInline>
            <Heading>Facilities</Heading>
            <InlineSpaced>
              <SearchWrapper>
                <SearchIcon>
                  <FontAwesomeIcon icon={faSearch} />
                </SearchIcon>
                <Search
                  name="search"
                  type="text"
                  placeholder="Search..."
                  onChange={e => handleSearch(e)}
                />
              </SearchWrapper>
              <TableMenu>
                <Option
                  type="button"
                  onClick={() => {
                    if (orderBy === "asc") setOrderBy("desc");
                    if (orderBy === "desc") setOrderBy("asc");
                  }}>
                  <Abbr title={orderBy === "desc" ? "Sort Z to A" : "Sort A to Z"}>
                    <FontAwesomeIcon icon={orderBy === "desc" ? faSortAlphaDown : faSortAlphaUp} />
                  </Abbr>
                </Option>
                {atLeast("admin") &&
                  (tableData?.length > 0 ||
                    (activeFilters && Object.keys(activeFilters)?.length > 0)) && (
                    <Option
                      type="button"
                      onClick={() => {
                        setShowModalFilters(!showModalFilters);
                      }}>
                      <Badge
                        count={filterCount}
                        title="Active filters"
                        offset="14px"
                        color={colors.heroGreen}
                      />
                      <FontAwesomeIcon icon={faFilter} />
                    </Option>
                  )}
                {roleCanAccessResource("facility", "export") &&
                  tableData &&
                  tableData.length > 0 && (
                    <Option
                      type="button"
                      onClick={() => {
                        setShowModalExport(true);
                      }}>
                      <abbr title="Export">
                        <FontAwesomeIcon icon={faFileExport} />
                      </abbr>
                    </Option>
                  )}
              </TableMenu>
            </InlineSpaced>
          </StyledInline>
        </TableMenuRow>
        <div ref={tableContent}>
          <BaseTable
            headings={{
              id: {header: "", disabled: true},
              name: {header: "Name", disabled: true},
              type: {header: "Type(s)", disabled: true},
              jobs: {header: "Job Number(s)", disabled: true},
              checksheets: {header: "Checksheets", disabled: true},
              events: {header: "Events", disabled: true}
            }}
            data={tableData}
            loading={loading}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
          />
        </div>
        <TableFooter>
          <Inline>
            <Text>
              <span>{total} total</span>
            </Text>
          </Inline>
        </TableFooter>
      </TableWrapper>
    </Modal>
  );
};

ModalViewAllFacilities.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired
};

// Style Overrides

const TableMenuRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${pad}px;
`;

const Option = styled(Button)`
  margin-left: ${pad}px;
`;

const NotifyList = styled(List)`
  list-style: circle;
`;

const NotifyListItem = styled(ListItem)`
  padding: ${pad / 2}px 0;
  margin-left: ${pad * 1.5}px;
`;

const TableLink = styled(Link)`
  font-weight: bold;
  align-self: flex-start;
  vertical-align: top;

  &:hover {
    color: ${({theme}) => theme.primary};
    transition: all ease 0.5s;
  }
`;

const Frequency = styled.span`
  text-transform: uppercase;
  padding: 0 ${pad / 2}px;
  color: ${({theme}) => theme.secondary};
  ${voice.quiet};
`;

const StyledInline = styled(Inline)`
  gap: ${pad}px;
  display: block;
  width: 100%;

  ${bp(1)} {
    display: flex;
  }
`;

const InlineSpaced = styled(Inline)`
  justify-content: space-between;
  width: 100%;
`;

export default ModalViewAllFacilities;
